import React, { useCallback, useMemo, useState } from 'react';
import { ResourceManager } from './components/ResourceManager';
import { supabase } from '@/lib/supabaseClient';
import { CheckCircle2, Eye, X } from 'lucide-react';

const statusOptions = [
  { label: 'New', value: 'new' },
  { label: 'In Review', value: 'in_review' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Closed', value: 'closed' },
];

const formatOrDash = (value) => (value ? value : '�');

const BookingsModule = () => {
  const [managerApi, setManagerApi] = useState(null);
  const [detailBooking, setDetailBooking] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [feedback, setFeedback] = useState('');

  const handleManagerReady = useCallback((api) => {
    setManagerApi(api);
  }, []);

  const handleConfirm = useCallback(
    async (booking) => {
      if (!booking || !booking.id) return;
      if (booking.status === 'contacted' || booking.status === 'closed') {
        setFeedback('Booking already processed.');
        return;
      }
      try {
        setConfirmingId(booking.id);
        const { error } = await supabase
          .from('bookings')
          .update({ status: 'contacted' })
          .eq('id', booking.id);
        if (error) throw error;
        setFeedback('Booking confirmed and moved to "Contacted".');
        managerApi?.refresh?.();
      } catch (err) {
        console.error('Failed to confirm booking', err);
        setFeedback('Failed to confirm booking.');
      } finally {
        setConfirmingId(null);
      }
    },
    [managerApi]
  );

  const handleView = useCallback((booking) => {
    setDetailBooking(booking);
  }, []);

  const columns = useMemo(
    () => [
      { label: 'Name', accessor: 'full_name' },
      { label: 'Phone', accessor: 'phone' },
      { label: 'Email', accessor: 'email' },
      { label: 'Property Ref', accessor: 'property_id', render: (value) => formatOrDash(value) },
      {
        label: 'Preferred Date',
        accessor: 'preferred_date',
        render: (value) => (value ? new Date(value).toLocaleDateString() : '�'),
      },
      {
        label: 'Preferred Time',
        accessor: 'preferred_time',
        render: (value) => formatOrDash(value),
      },
      { label: 'Source', accessor: 'source', render: (value) => formatOrDash(value) },
      {
        label: 'Notes',
        accessor: 'notes',
        render: (value) => (value ? value.slice(0, 80) + (value.length > 80 ? '�' : '') : '�'),
      },
      { label: 'Status', accessor: 'status' },
      {
        label: 'Requested At',
        accessor: 'created_at',
        render: (value) => (value ? new Date(value).toLocaleString() : '�'),
      },
      {
        label: 'Follow Up',
        accessor: 'id',
        render: (_value, record) => (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleView(record)}
              className="inline-flex items-center rounded border border-stone-300 px-2 py-1 text-xs font-medium text-stone-700 hover:bg-stone-50"
            >
              <Eye className="mr-1 h-3 w-3" /> View
            </button>
            <button
              type="button"
              onClick={() => handleConfirm(record)}
              disabled={confirmingId === record.id}
              className="inline-flex items-center rounded border border-lime-300 px-2 py-1 text-xs font-medium text-lime-700 hover:bg-lime-50 disabled:opacity-50"
            >
              <CheckCircle2 className="mr-1 h-3 w-3" /> {confirmingId === record.id ? 'Confirming�' : 'Confirm'}
            </button>
          </div>
        ),
      },
    ],
    [confirmingId, handleConfirm, handleView]
  );

  return (
    <div className="space-y-4">
      <ResourceManager
        table="bookings"
        title="Bookings"
        description="Track property viewing requests and payment plan onboarding."
        columns={columns}
        formFields={[
          { name: 'full_name', label: 'Full Name', placeholder: 'Prospect Name' },
          { name: 'phone', label: 'Phone', placeholder: '+256...' },
          { name: 'email', label: 'Email', placeholder: 'client@domain.com' },
          { name: 'property_id', label: 'Property ID / Ref', placeholder: 'PROP-001' },
          { name: 'preferred_date', label: 'Preferred Date', type: 'date' },
          { name: 'preferred_time', label: 'Preferred Time', type: 'time' },
          { name: 'status', label: 'Status', type: 'select', options: statusOptions },
          { name: 'notes', label: 'Notes', type: 'textarea' },
          { name: 'source', label: 'Source', placeholder: 'How they found us' },
        ]}
        defaultValues={{
          full_name: '',
          phone: '',
          email: '',
          property_id: '',
          preferred_date: '',
          preferred_time: '',
          status: 'new',
          notes: '',
          source: '',
        }}
        onReady={handleManagerReady}
      />
      {feedback && (
        <p className="text-sm text-stone-600">
          {feedback}
        </p>
      )}
      <BookingDetailModal booking={detailBooking} onClose={() => setDetailBooking(null)} />
    </div>
  );
};

const BookingDetailModal = ({ booking, onClose }) => {
  if (!booking) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-400">Booking Detail</p>
            <h3 className="text-xl font-semibold text-stone-900">{booking.full_name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-stone-200 p-2 text-stone-500 hover:text-stone-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <DetailItem label="Phone" value={booking.phone} />
          <DetailItem label="Email" value={booking.email} />
          <DetailItem label="Property Ref" value={booking.property_id} />
          <DetailItem label="Status" value={booking.status} />
          <DetailItem
            label="Preferred Date"
            value={booking.preferred_date ? new Date(booking.preferred_date).toLocaleDateString() : '�'}
          />
          <DetailItem label="Preferred Time" value={booking.preferred_time || '�'} />
          <DetailItem label="Source" value={booking.source || '�'} />
          <DetailItem
            label="Submitted"
            value={booking.created_at ? new Date(booking.created_at).toLocaleString() : '�'}
          />
        </dl>
        <div className="mt-6">
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-stone-400">Notes</p>
          <p className="rounded-xl border border-stone-100 bg-stone-50/70 p-4 text-sm text-stone-700">
            {booking.notes || 'No notes provided.'}
          </p>
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium uppercase tracking-[0.3em] text-stone-400">{label}</p>
    <p className="text-stone-800">{value || '�'}</p>
  </div>
);

export default BookingsModule;
